import {useCallback,useEffect,useRef,useState} from 'react';
import {ErrorNotice} from './components';
import {currentSession,errorDetails} from './lib/store';
import type {ErrorDetails} from './lib/types';
import {platform} from './platform/transport';

/** Provider scripts run in an HTTPS app window without native capabilities. */
export function DesktopPaymentWindow({path,onClose,active=true,accountPage=true,label='Payment'}:{
  path:string;onClose:()=>Promise<unknown>;active?:boolean;accountPage?:boolean;label?:string;
}){
  const started=useRef(false),pending=useRef(false),mounted=useRef(false),closed=useRef(onClose);
  const [busy,setBusy]=useState(false),[error,setError]=useState<ErrorDetails|null>(null);
  closed.current=onClose;
  const open=useCallback(async()=>{
    if(pending.current)return;
    pending.current=true;started.current=true;setBusy(true);setError(null);
    const account=currentSession(),failures:unknown[]=[];
    try{
      try{
        if(accountPage)await platform().openAccountPage(path);
        else await platform().openPage(new URL(path,platform().publicOrigin).href);
      }catch(error){failures.push(error);}
      if(mounted.current){
        try{
          const current=currentSession();
          if(accountPage&&(!account||current?.accountId!==account.accountId||current.csrfToken!==account.csrfToken))
            throw new Error('The account changed while the payment window was open.');
          await closed.current();
        }catch(error){failures.push(error);}
      }
      if(mounted.current&&failures.length>0)setError(errorDetails(failures.length===1?failures[0]:new AggregateError(failures,
        'The payment window and payment status could not be completed.',{cause:failures.map(errorDetails)})));
    }
    finally{pending.current=false;if(mounted.current)setBusy(false);}
  },[accountPage,path]);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  useEffect(()=>{if(active&&!started.current)void open();},[active,open]);
  return <section aria-label={`${label} window`}>
    <p role="status">{busy?`Complete ${label.toLowerCase()} in the EightBots payment window.`:'You can reopen the payment window.'}</p>
    <button className="button" type="button" disabled={busy} onClick={()=>void open()}>Open {label.toLowerCase()} window</button>
    <ErrorNotice error={error}/>
  </section>;
}
